/**
 * ============================================================
 * KEJJA OS — src/lib/communityApi.ts
 * ============================================================
 */

import { api } from '@/lib/api';

// ============================================================
// Types
// ============================================================

export type CommunityType = 'chama' | 'neighborhood' | 'fellowship' | 'family_extended' | 'general';
export type CommunityPostType = 'announcement' | 'event' | 'prayer_request' | 'general';

export interface Community {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  community_type: string;
  is_invite_only: boolean;
  join_code: string | null;
  max_homes: number;
  created_by_user_id: string | null;
  is_active: boolean;
  location_name: string | null;
  avatar_emoji: string | null;
  created_at: string;
  updated_at: string;
}

export interface CommunityWithRole extends Community {
  role: string;
}

export interface CommunityMember {
  home_id: string;
  role: string;
  home_name: string;
  home_motto: string | null;
  joined_at: string | null;
}

export interface CommunityPost {
  id: string;
  communityId: string;
  postedByHomeId: string;
  postedByUserId: string | null;
  postType: CommunityPostType;
  title: string | null;
  body: string;
  eventAt: string | null;
  eventLocation: string | null;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CommunityGoal {
  id: string;
  communityId: string;
  createdByHomeId: string;
  createdByUserId: string | null;
  name: string;
  description: string | null;
  icon: string | null;
  targetAmountMinor: number;
  currencyCode: string;
  targetDate: string | null;
  isComplete: boolean;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GoalWithProgress extends CommunityGoal {
  contributedMinor: number;
  contributorCount: number;
  percentComplete: number;
}

export interface CommunityContribution {
  id: string;
  communityId: string;
  goalId: string | null;
  contributedByHomeId: string;
  contributedByUserId: string | null;
  amountMinor: number;
  currencyCode: string;
  contributedOn: string;
  note: string | null;
  linkedTransactionId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CommunityPulse {
  postsCount: number;
  pinnedPosts: Array<{ id: string; title: string | null; body: string }>;
  recentPosts: Array<{ id: string; title: string | null; body: string; postType: string; createdAt: string }>;
  goalsCount: number;
  goalsOpen: number;
  goalsCompleted: number;
  totalContributedMinor: number;
  recentContributionsCount: number;
  memberCount: number;
}

export interface LinkableTransaction {
  id: string;
  amountMinor: number;
  currencyCode: string;
  occurredAt: string;
  description: string | null;
  merchant: string | null;
}

// ============================================================
// Payloads
// ============================================================

export interface CommunityCreatePayload {
  name: string;
  description?: string | null;
  communityType?: CommunityType;
  isInviteOnly?: boolean;
  maxHomes?: number;
  locationName?: string | null;
  avatarEmoji?: string | null;
}

export interface PostCreatePayload {
  postType?: CommunityPostType;
  title?: string | null;
  body: string;
  eventAt?: string | null;
  eventLocation?: string | null;
  isPinned?: boolean;
}
export type PostUpdatePayload = Partial<PostCreatePayload>;

export interface GoalCreatePayload {
  name: string;
  description?: string | null;
  icon?: string | null;
  targetAmountMinor: number;
  currencyCode?: string;
  targetDate?: string | null;
}
export type GoalUpdatePayload = Partial<GoalCreatePayload> & { isComplete?: boolean };

export interface ContributionCreatePayload {
  goalId?: string | null;
  amountMinor: number;
  currencyCode?: string;
  contributedOn: string;
  note?: string | null;
  linkedTransactionId?: string | null;
}

// ============================================================
// API
// ============================================================

interface CommunityApi {
  listMine: (homeId: string) => Promise<{ communities: CommunityWithRole[] }>;
  create: (homeId: string, body: CommunityCreatePayload) => Promise<{ community: Community }>;
  joinByCode: (homeId: string, joinCode: string) => Promise<{ community: Community }>;
  get: (homeId: string, communityId: string) => Promise<{ community: Community; members: CommunityMember[] }>;
  update: (homeId: string, communityId: string, body: Partial<CommunityCreatePayload>) => Promise<{ community: Community }>;
  regenerateCode: (homeId: string, communityId: string) => Promise<{ community: Community }>;
  leave: (homeId: string, communityId: string) => Promise<void>;

  pulse: (homeId: string, communityId: string) => Promise<CommunityPulse>;

  listPosts: (homeId: string, communityId: string) => Promise<{ posts: CommunityPost[] }>;
  createPost: (homeId: string, communityId: string, body: PostCreatePayload) => Promise<{ post: CommunityPost }>;
  updatePost: (homeId: string, communityId: string, postId: string, body: PostUpdatePayload) => Promise<{ post: CommunityPost }>;
  deletePost: (homeId: string, communityId: string, postId: string) => Promise<void>;

  listGoals: (homeId: string, communityId: string) => Promise<{ goals: GoalWithProgress[] }>;
  createGoal: (homeId: string, communityId: string, body: GoalCreatePayload) => Promise<{ goal: CommunityGoal }>;
  updateGoal: (homeId: string, communityId: string, goalId: string, body: GoalUpdatePayload) => Promise<{ goal: CommunityGoal }>;
  deleteGoal: (homeId: string, communityId: string, goalId: string) => Promise<void>;

  listContributions: (homeId: string, communityId: string, goalId?: string | null) => Promise<{ contributions: CommunityContribution[] }>;
  createContribution: (homeId: string, communityId: string, body: ContributionCreatePayload) => Promise<{ contribution: CommunityContribution }>;
  deleteContribution: (homeId: string, communityId: string, contributionId: string) => Promise<void>;

  listLinkableTransactions: (homeId: string, daysBack?: number) => Promise<{ transactions: LinkableTransaction[] }>;
}

const base = (homeId: string) => `/homes/${homeId}/community`;

export const communityApi: CommunityApi = {
  listMine: (homeId) =>
    api.get<{ communities: CommunityWithRole[] }>(`${base(homeId)}/communities`),
  create: (homeId, body) =>
    api.post<{ community: Community }>(`${base(homeId)}/communities`, body),
  joinByCode: (homeId, joinCode) =>
    api.post<{ community: Community }>(`${base(homeId)}/communities/join`, { joinCode }),
  get: (homeId, cId) =>
    api.get<{ community: Community; members: CommunityMember[] }>(`${base(homeId)}/communities/${cId}`),
  update: (homeId, cId, body) =>
    api.patch<{ community: Community }>(`${base(homeId)}/communities/${cId}`, body),
  regenerateCode: (homeId, cId) =>
    api.post<{ community: Community }>(`${base(homeId)}/communities/${cId}/regenerate-code`, {}),
  leave: (homeId, cId) =>
    api.post<void>(`${base(homeId)}/communities/${cId}/leave`, {}),

  pulse: (homeId, cId) =>
    api.get<CommunityPulse>(`${base(homeId)}/communities/${cId}/pulse`),

  listPosts: (homeId, cId) =>
    api.get<{ posts: CommunityPost[] }>(`${base(homeId)}/communities/${cId}/posts`),
  createPost: (homeId, cId, body) =>
    api.post<{ post: CommunityPost }>(`${base(homeId)}/communities/${cId}/posts`, body),
  updatePost: (homeId, cId, postId, body) =>
    api.patch<{ post: CommunityPost }>(`${base(homeId)}/communities/${cId}/posts/${postId}`, body),
  deletePost: (homeId, cId, postId) =>
    api.delete<void>(`${base(homeId)}/communities/${cId}/posts/${postId}`),

  listGoals: (homeId, cId) =>
    api.get<{ goals: GoalWithProgress[] }>(`${base(homeId)}/communities/${cId}/goals`),
  createGoal: (homeId, cId, body) =>
    api.post<{ goal: CommunityGoal }>(`${base(homeId)}/communities/${cId}/goals`, body),
  updateGoal: (homeId, cId, goalId, body) =>
    api.patch<{ goal: CommunityGoal }>(`${base(homeId)}/communities/${cId}/goals/${goalId}`, body),
  deleteGoal: (homeId, cId, goalId) =>
    api.delete<void>(`${base(homeId)}/communities/${cId}/goals/${goalId}`),

  listContributions: (homeId, cId, goalId) => {
    const qs = goalId === null ? '?goalId=null' : goalId ? `?goalId=${goalId}` : '';
    return api.get<{ contributions: CommunityContribution[] }>(`${base(homeId)}/communities/${cId}/contributions${qs}`);
  },
  createContribution: (homeId, cId, body) =>
    api.post<{ contribution: CommunityContribution }>(`${base(homeId)}/communities/${cId}/contributions`, body),
  deleteContribution: (homeId, cId, contributionId) =>
    api.delete<void>(`${base(homeId)}/communities/${cId}/contributions/${contributionId}`),

  listLinkableTransactions: (homeId, daysBack = 30) =>
    api.get<{ transactions: LinkableTransaction[] }>(`${base(homeId)}/linkable-transactions?daysBack=${daysBack}`),
};
